// React
import { createContext, ReactNode, useEffect, useReducer } from "react";

// Internal
import {
  endAllSessions,
  signInAccount,
  startAccountSession,
} from "src/common/accountSession";
import { resetLocalStorage } from "src/common/resetLocalStorage";
import { terminateFreshChatWidget } from "src/common/terminateFreshChatWidget";
import useCustomLogo from "src/hooks/useCustomLogo";
import { LogoutCred } from "src/redux/slices/credentials";
import { logoutOrganization } from "src/redux/slices/organization";
import { API_GATEWAY } from "../constants/settings";
import { PATH_AUTH } from "../routes/paths";
import { store } from "../redux/store";
import axios from "../utils/axios";

// Types
import {
  ActionMap,
  AuthState,
  AuthUser,
  IUser,
  JWTContextType,
} from "../@types/auth";

enum Types {
  Initial = "INITIALIZE",
  Login = "LOGIN",
  LoginTwoFA = "LOGIN_TWO_FA",
  LoginWithExternalProvider = "LOGIN_WITH_EXTERNAL_PROVIDER",
  LoginImpersonate = "LOGIN_IMPERSONATE",
  LoginAfterRegister = "LOGIN_AFTER_REGISTER",
  Logout = "LOGOUT",
  Register = "REGISTER",
  RegisterContinue = "REGISTERCONTINUE",
}

type JWTAuthPayload = {
  [Types.Initial]: {
    isAuthenticated: boolean;
    user: AuthUser;
  };
  [Types.Login]: {
    user: AuthUser;
  };
  [Types.LoginTwoFA]: {
    user: AuthUser;
  };
  [Types.LoginImpersonate]: {
    user: AuthUser;
  };
  [Types.LoginWithExternalProvider]: {
    user: AuthUser;
  };
  [Types.LoginAfterRegister]: {
    user: AuthUser;
  };
  [Types.Logout]: undefined;
  [Types.Register]: {
    user: AuthUser;
  };
  [Types.RegisterContinue]: {
    user: AuthUser;
  };
};

export type JWTActions =
  ActionMap<JWTAuthPayload>[keyof ActionMap<JWTAuthPayload>];

const initialState: AuthState = {
  isAuthenticated: false,
  isInitialized: false,
  user: null,
  is2FAPending: false,
};

const JWTReducer = (state: AuthState, action: JWTActions) => {
  switch (action.type) {
    case "INITIALIZE":
      return {
        isAuthenticated: action.payload.isAuthenticated,
        isInitialized: true,
        user: action.payload.user,
        is2FAPending: false,
      };
    case "LOGIN":
      return {
        ...state,
        isAuthenticated: true,
        user: action.payload.user,
        is2FAPending: false,
      };
    case "LOGIN_TWO_FA":
      return {
        ...state,
        isAuthenticated: false,
        is2FAPending: true,
        user: null,
      };
    case "LOGIN_IMPERSONATE":
      return {
        ...state,
        isAuthenticated: true,
        user: action.payload.user,
        is2FAPending: false,
      };
    case "LOGIN_WITH_EXTERNAL_PROVIDER":
      return {
        ...state,
        isAuthenticated: true,
        user: action.payload.user,
        is2FAPending: false,
      };
    case "LOGOUT":
      return {
        ...state,
        isAuthenticated: false,
        user: null,
        is2FAPending: false,
      };
    case "REGISTERCONTINUE":
      return {
        ...state,
        isAuthenticated: false,
        user: null,
        is2FAPending: false,
      };

    case "REGISTER":
      return {
        ...state,
        isAuthenticated: false,
        user: action.payload.user,
        is2FAPending: false,
      };
    case "LOGIN_AFTER_REGISTER":
      return {
        ...state,
        isAuthenticated: true,
        user: action.payload.user,
        is2FAPending: false,
      };

    default:
      return state;
  }
};

const AuthContext = createContext<JWTContextType | null>(null);

// ----------------------------------------------------------------------

type AuthProviderProps = {
  children: ReactNode;
};

function AuthProvider({ children }: AuthProviderProps) {
  const [state, dispatch] = useReducer(JWTReducer, initialState);
  const { setCustomLogo } = useCustomLogo();

  useEffect(() => {
    const initialize = async () => {
      const accessToken = store.getState()?.credential?.userCred.accessToken;
      try {
        if (accessToken) {
          // setSession(accessToken, refreshToken);
          dispatch({
            type: Types.Initial,
            payload: {
              isAuthenticated: true,
              user: null,
            },
          });
        } else {
          dispatch({
            type: Types.Initial,
            payload: {
              isAuthenticated: false,
              user: null,
            },
          });
        }
      } catch (err) {
        console.error(err);
        dispatch({
          type: Types.Initial,
          payload: {
            isAuthenticated: false,
            user: null,
          },
        });
      }
    };

    const stopAccountSession = startAccountSession(setCustomLogo);
    initialize();
    return stopAccountSession;
  }, []);

  const startSession = async (
    data: IUser,
    email: string,
    type: Types.Login | Types.LoginImpersonate | Types.LoginWithExternalProvider | Types.LoginAfterRegister,
  ) => {
    if (!(await signInAccount(data, email))) return;
    dispatch({ type, payload: { user: data.profile } });
    setCustomLogo();
  };

  const Login = async (username: string, password: string) => {
    const response = await axios.post(
      `${API_GATEWAY}/api/userservice/Account/login`,
      {
        username,
        password,
      },
    );
    if (!response.data?.requires2FACode) {
      await startSession(response.data, username, Types.Login);
    } else {
      dispatch({
        type: Types.LoginTwoFA,
        payload: {
          user: null,
        },
      });
    }
    return response.data;
  };

  const LoginTwoFA = async (
    username: string,
    password: string,
    code: string,
  ) => {
    const response = await axios.post(
      `${API_GATEWAY}/api/userservice/Account/login2fa`,
      {
        username,
        password,
        code,
      },
    );
    await startSession(response.data, username, Types.Login);
  };

  const LoginImpersonate = async (data: IUser) => {
    await startSession(data, data.profile?.email, Types.LoginImpersonate);
  };

  //Login with External Provider
  const LoginWithExternalProvider = async (response) => {
    localStorage.setItem(
      "discrepancyDetection",
      response.data.enableDiscrepancyDetection,
    );
    await startSession(
      response.data,
      response.data.username,
      Types.LoginWithExternalProvider,
    );
  };

  // Login After Register
  const LoginAfterRegister = async (username: string, password: string) => {
    const response = await axios.post(
      `${API_GATEWAY}/api/userservice/Account/login`,
      {
        username,
        password,
      },
    );
    await startSession(response.data, username, Types.LoginAfterRegister);
    return response.data;
  };

  // Pre-Register
  const register = async (SignupEmail: string) => {
    const response = await axios.post(
      `${API_GATEWAY}/api/usermanagement/account/newsignupemailrequest`,
      {
        SignupEmail,
      },
    );
    const { user } = response.data;

    dispatch({
      type: Types.Register,
      payload: {
        user,
      },
    });
  };

  const registerContinue = async (body) => {
    const response = await axios.post(
      `${API_GATEWAY}/api/webapi/v1/UserManagement`,
      body,
    );

    const { user, accessToken } = response.data.data;

    dispatch({
      type: Types.Register,
      payload: {
        user,
      },
    });
  };

  const logout = async () => {
    const reloadToLogin = endAllSessions();
    resetLocalStorage();
    terminateFreshChatWidget();
    store.dispatch(LogoutCred());
    store.dispatch(logoutOrganization());
    dispatch({ type: Types.Logout });
    if (reloadToLogin) window.location.replace(PATH_AUTH.login);
  };

  return (
    <AuthContext.Provider
      value={{
        ...state,
        method: "jwt",
        Login,
        LoginWithExternalProvider,
        LoginAfterRegister,
        LoginImpersonate,
        logout,
        register,
        registerContinue,
        LoginTwoFA,
      }}
    >
      {children}
    </AuthContext.Provider>
  );
}

export { AuthContext, AuthProvider };
